import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Label,
  Input,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  ListGroupItem,
  ListGroup,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import usePersistStoreData from "../../../Components/Hooks/usePersistStoreData";
import CommonLoading from "../../../Components/Common/CommonLoading";
import {
  ProfileDetailThunk,
  reKyc_AccountClosureThunk,
  reKyc_Esign_DocumentDownloadThunk,
} from "../../../slices/thunks";
import { showToast } from "../../../Components/ToastNotification";
import ConfirmationModal from "../../../Components/Modals/ConfirmationModal";

const AccountClosure = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { reKyc_AccountClosureLoading } = useSelector(
    (state) => state.Re_Kyc_Account_Closure
  );
  const { reKyc_Esign_DocumentLoading } = useSelector(
    (state) => state.Re_Kyc_Esign_Document_Download
  );
  // states for confirmation modal
  const [showModal, setShowModal] = useState({
    show: false,
    data: {},
  });
  const onCloseClick = () => {
    setShowModal({
      show: false,
      data: {},
    });
  };
  const { addInComeLoading } = useSelector((state) => state.Add_Annual_Income);

  const optionCloseReason = [
    { key: 1, label: "Employers Policy" },
    { key: 2, label: "Losses in Trading" },
    { key: 3, label: "Financial Constraints" },
    { key: 4, label: "This is Duplicate Account" },
    { key: 5, label: "Unhappy with Service" },
    { key: 6, label: "Unhappy with Platform" },
    { key: 7, label: "Others" },
  ];

  const initialValues = {
    account: "",
    closedReason: "",
    closedTradingAccount: "1",
    feedback: "",
    accountTransfer: "0",
    dematNumber: "",
    clientMasterUpload: null,
    signatureUpload: null,
  };

  const FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const SUPPORTED_FORMATS = ["image/png", "image/jpg", "image/jpeg"];

  const validationSchema = Yup.object({
    account: Yup.string().required("Please select the account"),
    feedback: Yup.string()
      .max(300, "Feedback cannot exceed 300 characters")
      .notRequired(),
    closedReason: Yup.string().required("Please select reasons to close"),
    closedTradingAccount: Yup.string().required("Please select upload type"),
    dematNumber: Yup.string().when("accountTransfer", {
      is: (val) => val == "1",
      then: (schema) => schema.required("Please enter Demat a/c number"),
      otherwise: (schema) => schema.notRequired(),
    }),
    clientMasterUpload: Yup.mixed().when("accountTransfer", {
      is: (val) => val == "1",
      then: (schema) =>
        schema
          .required("Please upload client master")
          .test(
            "fileType",
            "Only JPG, JPEG, PNG files are allowed",
            (value) => {
              return value && SUPPORTED_FORMATS.includes(value.type);
            }
          )
          .test("fileSize", "File size must be less than 2 MB", (value) => {
            return value && value.size <= FILE_SIZE;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    signatureUpload: Yup.mixed().when("accountTransfer", {
      is: (val) => val == "1",
      then: (schema) =>
        schema
          .required("Please upload signature")
          .test(
            "fileType",
            "Only JPG, JPEG, PNG files are allowed",
            (value) => {
              return value && SUPPORTED_FORMATS.includes(value.type);
            }
          )
          .test("fileSize", "File size must be less than 2 MB", (value) => {
            return value && value.size <= FILE_SIZE;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const openDigioPopup = (digioData) => {
    if (!window.Digio) {
      // console.error("Digio SDK not loaded");
      showToast(
        "Digio SDK is not loaded. Please refresh the page and try again.",
        "error"
      );
      setIsProcessing(false);
      return;
    }
    try {
      // Digio configuration with callback
      const options = {
        environment: "production",
        callback: function (response) {
          if (response.hasOwnProperty("error_code")) {
            console.log("Error occurred in process:", response);
            // Handle error
            setIsProcessing(false);
            showToast(response, "error");
            return;
          }
          showToast("Signing completed successfully", "success");
          onCloseClick();
          // console.log("Signing completed successfully:", response);
          dispatch(
            reKyc_Esign_DocumentDownloadThunk({
              params: { type: "account_closure" },
              token,
            })
          )
            .unwrap()
            .then((result) => {
              const blob = new Blob([result], { type: "application/pdf" });

              const fileURL = window.URL.createObjectURL(blob);

              // Open in new tab
              window.open(fileURL, "_blank");

              // Also download
              const link = document.createElement("a");
              link.href = fileURL;
              link.download = "account_closure.pdf"; // file name
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              // showToast("Downloaded successfully", "success");

              navigate("/profile");
              setIsProcessing(false);
            })
            .catch((error) => {
              showToast(
                error?.message ||
                  "Something went wrong. Please try again later",
                "error"
              );
              setIsProcessing(false);
            })

            .finally(() => {
              setIsProcessing(false);
            });
        },
        event_listener: function () {
          // Prevent Digio SDK from throwing when trying to call this
          // console.log("Digio event listener triggered");
          setIsProcessing(false);
        },
      };

      // Create new Digio instance
      const digio = new window.Digio(options);

      // Initialize Digio
      digio.init();
      digio.submit(digioData?.id, digioData?.identifier);
    } catch (error) {
      // console.error("Error opening Digio popup:", error);
      showToast("Error opening Digio: " + error.message, "error");
      setIsProcessing(false);
    }
  };

  const handleAccountClosure = (values) => {
    const formData = new FormData();
    formData.append("account", values?.account);
    formData.append("reason", values?.closedReason);
    formData.append("is_account_transfer", values?.accountTransfer);
    formData.append("is_close_trading_account", values?.closedTradingAccount);
    if (values.accountTransfer == "1") {
      formData.append("demat", values?.dematNumber);
      formData.append("client_master_upload", values?.clientMasterUpload);
      formData.append("signature_upload", values?.signatureUpload);
    }

    if (values?.feedback) {
      formData.append("feedback", values?.feedback);
    }

    dispatch(reKyc_AccountClosureThunk({ body: formData, token }))
      .then((res) => {
        if (res?.payload?.code === 200) {
          showToast(res?.payload?.message, "success");
          openDigioPopup(res?.payload?.data);
        } else {
          showToast(
            res?.error?.message ||
              "Something went wrong, please try again later",
            "error"
          );
        }
      })
      .catch((err) => {
        showToast(
          err || "Something went wrong, please try again later",
          "error"
        );
      });
  };

  const onSubmit = (values, { resetForm }) => {
    setShowModal({
      show: true,
      data: {},
      title: "Please Confirm Account Closure",
      bodyContent:
        "I/We understand you're considering closing your account. To proceed, please be aware that you'll lose access to features and your account data will be deleted",
      cancelText: "Close",
      confirmText: "Confirm",
      onConfirm: () => {
        handleAccountClosure(values);
        // resetForm();
      },
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {loading ? (
            <div
              className="centerContent h-100 w-100"
              style={{ minHeight: "80vh" }}
            >
              <CommonLoading />
            </div>
          ) : (
            <>
              <div className="position-relative mx-n4 mt-n4">
                <div
                  className="profile-wid-bg profile-setting-img"
                  style={{ backgroundColor: "#01256D" }}
                >
                  <div className="overlay-content">
                    <div className="text-end p-3">
                      <div className="p-0 ps-2 ms-auto rounded-circle profile-photo-edit">
                        <Nav
                          pills
                          className=" animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
                          role="tablist"
                        >
                          <NavItem>
                            <NavLink
                              // className={classnames(" ", {
                              //   active: activeTab === "1",
                              // })}
                              onClick={() => {
                                navigate("/edit_option");
                              }}
                              style={{
                                backgroundColor: "transparent",
                                border: "1px solid #0FA67B",
                              }}
                            >
                              <svg
                                className="me-2"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3.99 9L16 9L16 7L3.99 7L3.99 4L3.49691e-07 8L3.99 12L3.99 9Z"
                                  fill="#0FA67B"
                                />
                              </svg>
                              <span className="d-none d-md-inline-block">
                                Go Back
                              </span>
                            </NavLink>
                          </NavItem>
                        </Nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Row>
                <Col xxl={12} xl={12}>
                  <Card className="mt-n5">
                    <CardHeader>
                      <h5 className="card-title mb-0">Account Closure</h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="row m-0 p-0">
                        <Formik
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          onSubmit={onSubmit}
                        >
                          {({
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }) => (
                            <Form className="px-0">
                              <Row>
                                <Col lg={6} className=" mb-3">
                                  <Label
                                    htmlFor="account"
                                    className="form-label"
                                  >
                                    Select CDSL/NSDL Account
                                  </Label>
                                  <Input
                                    type="select"
                                    id="account"
                                    name="account"
                                    placeholder="CDSL/NSDL"
                                    value={values.account}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="form-control"
                                  >
                                    <option value="" disabled>
                                      Select
                                    </option>
                                    <option value="1">CDSL</option>
                                    <option value="2">NSDL</option>
                                  </Input>
                                  <ErrorMessage
                                    name="account"
                                    component="div"
                                    className="text-danger mt-1"
                                  />
                                </Col>

                                <Col lg={6} className=" mb-3">
                                  <Label
                                    htmlFor="closedReason"
                                    className="form-label"
                                  >
                                    Reasons to Close Your Account
                                  </Label>
                                  <Input
                                    type="select"
                                    id="closedReason"
                                    name="closedReason"
                                    value={values.closedReason}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="form-control"
                                  >
                                    <option value="" disabled>
                                      Select
                                    </option>
                                    {optionCloseReason?.map((item) => (
                                      <option value={item?.key} key={item?.key}>
                                        {item?.label}
                                      </option>
                                    ))}
                                  </Input>
                                  <ErrorMessage
                                    name="closedReason"
                                    component="div"
                                    className="text-danger mt-1"
                                  />
                                </Col>

                                <Col lg={6} className=" mb-3">
                                  <Label
                                    htmlFor="feedback"
                                    className="form-label"
                                  >
                                    Feedback
                                  </Label>
                                  <Input
                                    type="textarea"
                                    id="feedback"
                                    name="feedback"
                                    placeholder="Please provide your feedback"
                                    value={values.feedback}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="form-control"
                                  ></Input>
                                  <ErrorMessage
                                    name="feedback"
                                    component="div"
                                    className="text-danger mt-1"
                                  />
                                </Col>
                                <Row className="">
                                  <Col lg={4} className="mb-3">
                                    <Label className="form-label">
                                      Close Trading Account?
                                    </Label>
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="form-check">
                                        <Input
                                          type="radio"
                                          id="closedTradingAccountYes"
                                          name="closedTradingAccount"
                                          value="1"
                                          checked={
                                            values.closedTradingAccount === "1"
                                          }
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          className="form-check-input"
                                        />
                                        <Label
                                          check
                                          htmlFor="closedTradingAccountYes"
                                          className="form-check-label"
                                        >
                                          Yes
                                        </Label>
                                      </div>
                                      <div className="form-check">
                                        <Input
                                          type="radio"
                                          id="closedTradingAccountNo"
                                          name="closedTradingAccount"
                                          value="0"
                                          checked={
                                            values.closedTradingAccount === "0"
                                          }
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          className="form-check-input"
                                        />
                                        <Label
                                          check
                                          htmlFor="closedTradingAccountNo"
                                          className="form-check-label"
                                        >
                                          No
                                        </Label>
                                      </div>
                                    </div>
                                    <ErrorMessage
                                      name="closedTradingAccount"
                                      component="div"
                                      className="text-danger mt-1"
                                    />
                                  </Col>

                                  <Col lg={4} className="mb-3">
                                    <Label className="form-label">
                                      Account Transfer ?
                                    </Label>
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="form-check">
                                        <Input
                                          type="radio"
                                          id="accountTransfer"
                                          name="accountTransfer"
                                          value="1"
                                          checked={
                                            values.accountTransfer == "1"
                                          }
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          className="form-check-input"
                                        />
                                        <Label
                                          check
                                          htmlFor="accountTransfer"
                                          className="form-check-label"
                                        >
                                          Yes
                                        </Label>
                                      </div>
                                      <div className="form-check">
                                        <Input
                                          type="radio"
                                          id="accountTransfer"
                                          name="accountTransfer"
                                          value="0"
                                          checked={
                                            values.accountTransfer === "0"
                                          }
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          className="form-check-input"
                                        />
                                        <Label
                                          check
                                          htmlFor="closedTradingAccount"
                                          className="form-check-label"
                                        >
                                          No
                                        </Label>
                                      </div>
                                    </div>
                                    <ErrorMessage
                                      name="closedTradingAccount"
                                      component="div"
                                      className="text-danger mt-1"
                                    />
                                  </Col>
                                  {values?.accountTransfer == "1" && (
                                    <>
                                      <Col lg={6} className=" mb-3">
                                        <Label
                                          htmlFor="dematNumber"
                                          className="form-label"
                                        >
                                          Demat of which you are transfering to
                                        </Label>
                                        <Input
                                          type="text"
                                          id="dematNumber"
                                          name="dematNumber"
                                          placeholder="Demat a/c number"
                                          value={values.dematNumber}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          className="form-control"
                                        ></Input>
                                        <ErrorMessage
                                          name="dematNumber"
                                          component="div"
                                          className="text-danger mt-1"
                                        />
                                      </Col>
                                      <Col lg={6} className="mb-3">
                                        <Label
                                          htmlFor="clientMasterUpload"
                                          className="form-label"
                                        >
                                          Client Master Upload
                                        </Label>
                                        <Input
                                          type="file"
                                          id="clientMasterUpload"
                                          name="clientMasterUpload"
                                          accept="image/png, image/jpg, image/jpeg"
                                          onChange={(e) => {
                                            setFieldValue(
                                              "clientMasterUpload",
                                              e.currentTarget.files[0]
                                            );
                                          }}
                                          onBlur={handleBlur}
                                          className="form-control"
                                        />
                                        <ErrorMessage
                                          name="clientMasterUpload"
                                          component="div"
                                          className="text-danger mt-1"
                                        />
                                      </Col>
                                      <Col lg={6} className="mb-3">
                                        <Label
                                          htmlFor="signatureUpload"
                                          className="form-label"
                                        >
                                          Signature Upload
                                        </Label>
                                        <Input
                                          type="file"
                                          id="signatureUpload"
                                          name="signatureUpload"
                                          accept="image/png, image/jpg, image/jpeg"
                                          onChange={(e) => {
                                            setFieldValue(
                                              "signatureUpload",
                                              e.currentTarget.files[0]
                                            );
                                          }}
                                          onBlur={handleBlur}
                                          className="form-control"
                                        />
                                        <ErrorMessage
                                          name="signatureUpload"
                                          component="div"
                                          className="text-danger mt-1"
                                        />
                                      </Col>
                                    </>
                                  )}
                                </Row>
                                <div className="text-end ">
                                  <button
                                    type="submit"
                                    className="btn btn-primary btn-md fs-16"
                                    style={{ minWidth: "100px" }}
                                    disabled={addInComeLoading}
                                  >
                                    {addInComeLoading ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      "Proceed to e-Sign"
                                    )}
                                  </button>
                                </div>
                              </Row>
                            </Form>
                          )}
                        </Formik>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
      {showModal?.show && (
        <ConfirmationModal
          confirm_modal={showModal?.show}
          close_confirmModal={onCloseClick}
          title={showModal?.title}
          bodyContent={showModal?.bodyContent}
          cancelText={showModal?.cancelText}
          confirmText={showModal?.confirmText}
          handleConfirmDelete={showModal?.onConfirm}
          loading={
            isProcessing ||
            reKyc_AccountClosureLoading ||
            reKyc_Esign_DocumentLoading
          }
        />
      )}
    </React.Fragment>
  );
};

export default AccountClosure;
